import { feathers } from "@feathersjs/feathers";
import socketio from "@feathersjs/socketio-client";
import authentication from "@feathersjs/authentication-client";
import io from "socket.io-client";
import type { Application, TransportConnection } from "@feathersjs/feathers";
// The localforage-shaped async storage contract, as a REAL exported type. It
// used to be the bare ambient global `LocalForage`, which only resolved while
// some other file in the program happened to `import "localforage"` — deleting
// the last such import (the DatabaseManager's dead second RootStore) broke the
// name here. `localforage` satisfies this structurally.
import type { KeyValueStorage } from "@intrasync/stores";
// The api client runs BEFORE any store exists (it builds the socket and does
// the pre-connect token refresh), so it cannot reach `rootStore.diagnostics`.
// `emitAppDiagnostic` is the same pipeline reached from module scope — see
// src/diagnostics/diagnosticsConfig.ts. Everything below that used to be a
// bare `console.warn` on a REAL failure now also lands in the retained ring
// buffer the admin panel reads; the `debugMode` tracing is left on console
// alone, because it is tracing, not diagnosis.
import { emitAppDiagnostic } from "../diagnostics/diagnosticsConfig";
import { tokenSubject } from "../stores/sessionGuard";
import { linkFor, settleBufferedCalls } from "./socketLink";
// import rest from '@feathersjs/rest-client';

// Assuming createClient is a function you need to import from a specific module
// import { createClient } from '../../api/src/client'; // Adjust the path as necessary
declare const window: Window;

interface ConnectionInstances {
  [key: string]: Application; // Add an index signature
}

const createClient = (
  connection: TransportConnection,
  authenticationOptions = {},
) => {
  const client = feathers();

  client.configure(connection);
  client.configure(authentication(authenticationOptions));
  client.set("connection", connection);
  return client;
};

const connectionInstances: ConnectionInstances = {};

// VITE_API_URL overrides the dev API address, as config/environment.ts
// already honours it. Without this the client was pinned to :3389, so two
// checkouts on one box could not each run their own API: the second one's
// app silently talked to the first one's server.
const devUri =
  (typeof import.meta !== "undefined" &&
    (import.meta.env?.VITE_API_URL as string | undefined)) ||
  "http://localhost:3389";
const stageUri = "https://stage.hq-api.intrasync.app";
const prodUri = "https://hq-api.intrasync.app";

type Mode =
  | "dev"
  | "development"
  | "stage"
  | "beta"
  | "live"
  | "prod"
  | "production"
  | "test";

const uriMap: Record<Mode, string> = {
  dev: devUri,
  development: devUri,
  stage: stageUri,
  beta: stageUri,
  live: prodUri,
  prod: prodUri,
  production: prodUri,
  test: "",
};

interface ApiOptions {
  debugMode?: boolean;
  authStorageKey?: string;
  refreshStorageKey?: string;
  enableSocketAuth?: boolean;
  restClient?: boolean;
  enableFindOne?: boolean;
  storage?: Storage | KeyValueStorage;
  uriOverride?: Record<string, string>;
}

interface SocketOptions {
  path: string;
  transports: string[];
  forceNew: boolean;
  pingInterval: number;
  pingTimeout: number;
  timeout?: number;
  query?: {
    Authorization?: string;
  };
}

interface FirstLoadAuth {
  accessToken?: string;
  refreshToken?: string;
  deviceId?: string;
  userId?: string;
  authPath?: string;
}

interface AuthOptions {
  path?: string;
  storageKey: string;
  storage: Storage | KeyValueStorage;
}

/**
 * Check if a JWT token is expired
 * @param token - JWT token string
 * @returns true if expired or invalid, false if valid
 */
const isTokenExpired = (token: string | null): boolean => {
  if (!token) return true;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return true;

    const payload = JSON.parse(atob(parts[1]));
    if (!payload.exp) return true;

    // Check if token expires within 60 seconds (add buffer for network latency)
    const expiryTime = payload.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    const bufferTime = 60 * 1000; // 60 second buffer

    return currentTime >= expiryTime - bufferTime;
  } catch {
    return true;
  }
};

/** How long the socket waits for a token refresh before it connects without one. */
const REFRESH_REQUEST_TIMEOUT_MS = 15000;

/** The longest wait between retries of a handshake the server refused (see `connect_error` below). */
export const REFUSED_HANDSHAKE_RETRY_MAX_MS = 60_000;

/**
 * Refresh the access token using the refresh token
 * @param uri - API base URI
 * @param refreshToken - Refresh token
 * @param userId - User ID
 * @param deviceId - Device ID
 * @returns New access token or null if refresh failed
 */
const refreshAccessToken = async (
  uri: string,
  refreshToken: string,
  userId: string,
  deviceId: string,
  debugMode = false,
): Promise<string | null> => {
  try {
    const refreshUrl = `${uri}/refresh-token`;
    if (debugMode) {
      console.warn(
        "apiClient debugMode: refreshing expired access token before socket connection",
        {
          url: refreshUrl,
          userId,
          deviceId,
          hasRefreshToken: !!refreshToken,
          refreshTokenLength: refreshToken?.length,
        },
      );
    }

    const response = await fetch(refreshUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        refreshToken,
        userId: Number(userId),
        deviceId,
      }),
      // The socket now waits for this answer before it opens (see the
      // pre-connect and reconnect refreshes below), so a request that never
      // answers must end: a timeout is a failed refresh, and the socket then
      // connects with what it has, as it does for any other failure.
      ...(typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function"
        ? { signal: AbortSignal.timeout(REFRESH_REQUEST_TIMEOUT_MS) }
        : {}),
    });

    if (debugMode) {
      console.warn("apiClient debugMode: token refresh response", {
        status: response.status,
        ok: response.ok,
        statusText: response.statusText,
      });
    }

    if (!response.ok) {
      const errorText = await response.text().catch(() => "Unknown error");
      emitAppDiagnostic(
        "warn",
        "apiClient",
        "auth.refresh_rejected",
        `Token refresh rejected with HTTP ${response.status}`,
        { data: { status: response.status, statusText: response.statusText } },
      );
      console.warn("Token refresh HTTP failed:", response.status, errorText);
      return null;
    }

    const data = await response.json();
    if (debugMode) {
      console.warn("apiClient debugMode: token refresh data", {
        hasAccessToken: !!data.accessToken,
        accessTokenLength: data.accessToken?.length,
        keys: Object.keys(data),
      });
    }

    if (data.accessToken) {
      return data.accessToken;
    }

    emitAppDiagnostic(
      "warn",
      "apiClient",
      "auth.refresh_failed",
      "Token refresh succeeded but returned no accessToken",
      { data: { keys: Object.keys(data ?? {}) } },
    );
    console.warn("Token refresh response missing accessToken:", data);
    return null;
  } catch (error) {
    emitAppDiagnostic(
      "warn",
      "apiClient",
      "auth.refresh_failed",
      "Token refresh network error",
      { error },
    );
    console.warn("Token refresh network error:", error);
    return null;
  }
};

/** A structurally-valid JWT (three non-empty parts) — all the client checks before trusting a stored token. */
const isValidToken = (token: string | null): boolean => {
  if (!token) return false;
  const parts = token.split(".");
  return parts.length === 3 && parts.every((part) => part.length > 0);
};

const apiClient = (
  mode: Mode,
  apiOptions: ApiOptions = {
    debugMode: false,
    authStorageKey: "feathers-jwt-dev",
    refreshStorageKey: "refresh-token-dev",
    enableSocketAuth: true,
    restClient: false,
    enableFindOne: true,
  },
): Application => {
  // rootStoreConfig maps any Vite mode that is neither "development" nor
  // "production" (e.g. MODE=staging / beta) to "test", which below yields a
  // client with NO transport configured — every service call would hang. A
  // real test runner has import.meta.env.MODE === "test"; for any other build
  // that lands here, fall back to the production transport instead.
  if (mode === "test") {
    const buildMode =
      typeof import.meta !== "undefined" ? import.meta.env?.MODE : undefined;
    if (buildMode && buildMode !== "test") {
      emitAppDiagnostic(
        "warn",
        "apiClient",
        "store.fallback",
        `Unrecognized build mode "${buildMode}" mapped to "test" (transportless); falling back to the production transport`,
        { data: { buildMode } },
      );
      console.warn(
        `apiClient: unrecognized build mode "${buildMode}" was mapped to "test" (transportless); falling back to the production transport.`,
      );
      mode = "production";
    }
  }
  let socketAuthentication = false;
  const {
    debugMode,
    authStorageKey,
    refreshStorageKey,
    storage,
    uriOverride,
    // enableFindOne,
  } = apiOptions;

  if (debugMode) {
    console.warn("apiClient debugMode enabled");
    console.warn("apiClient debugMode apiOptions", apiOptions);
  }
  // console.log('feathers in', mode, 'mode');

  if (debugMode) {
    console.warn("apiClient debugMode: authStorageKey", authStorageKey);
  }
  if (connectionInstances[mode]) {
    if (debugMode) {
      console.warn(
        "apiClient debugMode: connectionInstances[mode]",
        mode,
        connectionInstances[mode],
      );
    }
    return connectionInstances[mode];
  } else {
    if (mode === "test") {
      connectionInstances[mode] = feathers().configure(authentication());
      if (debugMode) {
        console.warn(
          "apiClient debugMode: connectionInstances[mode]",
          mode,
          connectionInstances[mode],
        );
      }
      return connectionInstances[mode];
    }
    let uri = uriMap[mode];
    if (debugMode) {
      console.warn("apiClient debugMode: uri", uri);
    }
    if (uriOverride && Object.keys(uriOverride).length > 0) {
      const uriWithOverrides = { ...uriMap, ...uriOverride };
      uri = uriWithOverrides[mode];
      if (debugMode) {
        console.warn("apiClient debugMode: uriOverride set uri", uri);
      }
    }
    const authStorage = storage ? storage : window.localStorage;
    if (debugMode) {
      console.warn("apiClient debugMode: authStorage", authStorage);
    }
    let authOptions: AuthOptions = {
      storageKey: authStorageKey || "intrasync-jwt-dev",
      storage: authStorage,
    };

    const authPath = `/authentication`; // defaults to /authentication
    if (debugMode) {
      console.warn(
        "apiClient debugMode: Adding authPath to authOptions.path",
        authPath,
      );
    }
    authOptions = {
      ...authOptions,
      // path: authPath,
    };

    if (debugMode) {
      console.warn("apiClient debugMode: authOptions", authOptions);
    }

    // if (apiOptions.restClient) {
    //   const apiRestClient = rest(uri);

    //   connectionInstances[mode] = feathers()
    //     .configure(apiRestClient.fetch(fetch))
    //     .configure(authentication(authOptions));

    //   if (debugMode) {
    //     console.warn(
    //       'apiClient debugMode: connectionInstances[mode]',
    //       mode,
    //       connectionInstances[mode]
    //     );
    //   }
    //   return connectionInstances[mode];
    // }

    let socketOptions: SocketOptions = {
      path: "/socket.io",
      transports: ["websocket"],
      forceNew: true,
      pingInterval: 25000, // Increased for stability
      pingTimeout: 60000, // Increased timeout
      timeout: 10000, // Increased connection timeout
    };
    const firstLoadAuth: FirstLoadAuth = {};

    // Resolve storage keys - handle both "dev" and "development" modes
    const resolvedAuthStorageKey =
      authStorageKey ||
      (mode === "development"
        ? "feathers-jwt-development"
        : `feathers-jwt-${mode}`);
    const resolvedRefreshStorageKey =
      refreshStorageKey ||
      (mode === "development"
        ? "refresh-token-development"
        : `refresh-token-${mode}`);

    const userIdStorageKey = mode.includes("prod") ? "userId" : `userId-${mode}`;

    /**
     * Make `firstLoadAuth` say what the storage every tab shares says NOW:
     * each credential present is taken, each one gone is dropped. Called when a
     * refresh this client started was overtaken by another tab's sign-in or
     * sign-out — storage belongs to that session, and the handshake must not
     * present the credentials the overtaken refresh was spending.
     */
    const resyncFirstLoadAuth = () => {
      const read = (key: string) => {
        try {
          return window.localStorage.getItem(key);
        } catch {
          return null;
        }
      };
      const accessToken = read(resolvedAuthStorageKey);
      const refreshToken = read(resolvedRefreshStorageKey);
      const deviceId = read("deviceId");
      const userId = read(userIdStorageKey);
      if (isValidToken(accessToken)) firstLoadAuth.accessToken = accessToken!;
      else delete firstLoadAuth.accessToken;
      if (isValidToken(refreshToken)) firstLoadAuth.refreshToken = refreshToken!;
      else delete firstLoadAuth.refreshToken;
      if (deviceId) firstLoadAuth.deviceId = deviceId;
      else delete firstLoadAuth.deviceId;
      if (userId) firstLoadAuth.userId = userId;
      else delete firstLoadAuth.userId;
    };

    /** Who a handshake built from `firstLoadAuth` signs the connection in as (the `sub` of its refresh, else access, token). */
    const subjectOf = (auth: FirstLoadAuth): string | undefined =>
      tokenSubject(auth.refreshToken) ?? tokenSubject(auth.accessToken);
    /**
     * Whom the connection is signed in as, from the Feathers client's own
     * `login` and `logout` events (set below, once the client exists): the
     * person whose calls sit in socket.io's offline send buffer when a
     * reconnect finds the credentials have moved on (settleBufferedCalls).
     * Not what the last HANDSHAKE presented: a sign-in made over the open
     * socket (the login page) never goes through a handshake. Undefined means
     * nobody is known to be signed in, which is no evidence about the buffer.
     *
     * It starts as the person the page LOADED with (set once `firstLoadAuth`
     * is read, below): the Feathers client re-authenticates from storage and
     * the app starts its loads as soon as the client is built, so those calls
     * queue in the send buffer, owned by that person, before any `login` fires.
     * A page loaded signed out has no such person, and a later sign-in on it
     * is not a change.
     */
    let signedInSubject: string | undefined;

    /** The handshake query `firstLoadAuth` earns (same minimum and size limit as the first load), or none. */
    const handshakeAuth = (): string | undefined => {
      if (
        !firstLoadAuth.deviceId ||
        !(firstLoadAuth.refreshToken || firstLoadAuth.accessToken)
      ) {
        return undefined;
      }
      const payload = JSON.stringify(firstLoadAuth);
      return payload.length < 2048 ? payload : undefined;
    };

    /**
     * Make the NEXT handshake present what the storage every tab shares says
     * now (or nothing, when it holds no session): `firstLoadAuth`, the
     * manager's query and the socket's `auth`. Synchronous, because
     * socket.io opens the transport the moment it has announced a connection
     * attempt; anything that must be in the handshake has to be there by then.
     */
    const installHandshake = () => {
      resyncFirstLoadAuth();
      const authPayload = handshakeAuth();
      if (socket.io && socket.io.opts) {
        if (authPayload) socket.io.opts.query = { Authorization: authPayload };
        else delete socket.io.opts.query;
      }
      socket.auth = authPayload ? { Authorization: authPayload } : undefined;
    };
    /** A handshake that presents nothing. */
    const clearHandshake = () => {
      if (socket.io && socket.io.opts) delete socket.io.opts.query;
      socket.auth = undefined;
    };

    if (debugMode) {
      console.warn("apiClient debugMode: socketOptions", socketOptions);
    }

    if (apiOptions.enableSocketAuth && window && window.localStorage) {
      if (debugMode) {
        console.warn("apiClient debugMode: window", window);
      }

      try {
        // Batch localStorage reads for better performance
        const userIdKey = userIdStorageKey;

        const [accessToken, refreshToken, deviceId, userId] = [
          window.localStorage.getItem(resolvedAuthStorageKey),
          window.localStorage.getItem(resolvedRefreshStorageKey),
          window.localStorage.getItem("deviceId"),
          window.localStorage.getItem(userIdKey),
        ];

        if (debugMode) {
          console.warn("apiClient debugMode: batched auth data", {
            hasAccessToken: !!accessToken,
            hasRefreshToken: !!refreshToken,
            hasDeviceId: !!deviceId,
            hasUserId: !!userId,
          });
        }

        // Validate tokens before using them (isValidToken: structure only)
        if (isValidToken(accessToken)) {
          firstLoadAuth.accessToken = accessToken!;
        }

        if (isValidToken(refreshToken)) {
          firstLoadAuth.refreshToken = refreshToken!;
        }

        if (deviceId) {
          firstLoadAuth.deviceId = deviceId;
        }

        if (userId) {
          firstLoadAuth.userId = userId;
        }

        if (authOptions && authOptions.path) {
          firstLoadAuth.authPath = authOptions.path;
        }

        // Only setup socket auth if we have minimum required data
        if (
          firstLoadAuth.deviceId &&
          (firstLoadAuth.refreshToken || firstLoadAuth.accessToken)
        ) {
          try {
            const Auth = JSON.stringify(firstLoadAuth);
            // Limit auth payload size to prevent socket issues
            if (Auth.length < 2048) {
              socketOptions = {
                ...socketOptions,
                query: {
                  Authorization: Auth,
                },
              };
              socketAuthentication = true;

              if (debugMode) {
                console.warn("apiClient debugMode: socket auth enabled", {
                  authPayloadSize: Auth.length,
                  hasToken: !!firstLoadAuth.accessToken,
                  hasRefresh: !!firstLoadAuth.refreshToken,
                });
              }
            } else {
              emitAppDiagnostic(
                "warn",
                "apiClient",
                "auth.init_failed",
                "Auth payload too large for the socket query; socket auth skipped",
                { data: { authPayloadSize: Auth.length } },
              );
              console.warn(
                "apiClient: Auth payload too large, skipping socket auth",
              );
            }
          } catch (error) {
            emitAppDiagnostic(
              "warn",
              "apiClient",
              "auth.init_failed",
              "Failed to serialize auth data for socket auth",
              { error },
            );
            console.warn("apiClient: Failed to serialize auth data:", error);
          }
        } else {
          if (debugMode) {
            console.warn(
              "apiClient debugMode: insufficient auth data for socket auth",
              {
                hasDeviceId: !!firstLoadAuth.deviceId,
                hasTokens: !!(
                  firstLoadAuth.refreshToken || firstLoadAuth.accessToken
                ),
              },
            );
          }
        }
      } catch (error) {
        emitAppDiagnostic(
          "error",
          "apiClient",
          "auth.init_failed",
          "Error setting up socket authentication",
          { error },
        );
        console.warn(
          "apiClient: Error setting up socket authentication:",
          error,
        );
      }
    }

    // The owner of whatever the startup calls queue in the send buffer.
    const loadedSubject = subjectOf(firstLoadAuth);
    signedInSubject = loadedSubject;

    // Check if access token is expired and needs refresh before connecting
    let tokenNeedsRefresh = false;
    if (
      firstLoadAuth.accessToken &&
      isTokenExpired(firstLoadAuth.accessToken)
    ) {
      tokenNeedsRefresh = true;
    }

    // When the first connection has to wait for a refresh, the socket must not
    // connect by itself: socket.io connects as soon as it is built, with the
    // credentials the page loaded with. If another tab replaced the session
    // while the refresh ran, that connection would already be authenticated as
    // the previous person, and the connect below would be a no-op. So it is
    // built with autoConnect off, and connected exactly once, on every branch
    // of the refresh (a refresh that cannot run - no refresh token, user id or
    // device id to spend - leaves the default: nothing to wait for).
    const holdFirstConnect = !!(
      tokenNeedsRefresh &&
      firstLoadAuth.refreshToken &&
      firstLoadAuth.userId &&
      firstLoadAuth.deviceId
    );
    const socket = io(uri, {
      ...socketOptions,
      autoConnect: !holdFirstConnect,
    });

    const socketLink = linkFor(socket);
    // suspendSocket forgets what the handshake would present.
    socketLink.forgetAuth = () => {
      signedInSubject = undefined;
      for (const key of ["accessToken", "refreshToken", "userId"] as const) {
        delete firstLoadAuth[key];
      }
    };

    /**
     * The send buffer is part of the credential transition (see
     * settleBufferedCalls): call it once the next handshake is installed and
     * before the connection that flushes it opens.
     */
    const settleBuffer = (options: { anonymous?: boolean; subjectBefore?: string } = {}) => {
      const result = settleBufferedCalls(socket, {
        subjectBefore: options.subjectBefore ?? signedInSubject,
        subjectNow: subjectOf(firstLoadAuth),
        // Nobody to present: nothing in storage, or a handshake deliberately left empty.
        signedOut: options.anonymous === true || (!firstLoadAuth.accessToken && !firstLoadAuth.refreshToken),
        accessToken: firstLoadAuth.accessToken ?? null,
      });
      if (debugMode && (result.dropped || result.rewritten)) {
        console.warn("apiClient debugMode: send buffer settled", result);
      }
    };

    if (holdFirstConnect) {
      const startedIn = socketLink.generation;
      // Whom the calls made while the first connect is held belong to: nobody
      // has signed in over a socket that is not connected yet, so it is the
      // person the page loaded with (loadedSubject, above).
      (async () => {
        try {
          const newAccessToken = await refreshAccessToken(
            uri,
            firstLoadAuth.refreshToken!,
            firstLoadAuth.userId!,
            firstLoadAuth.deviceId!,
            debugMode,
          );

          // The connection was torn down while this refresh was on the wire
          // (this tab refused a foreign credential - suspendSocket): it opens
          // again only when resumeSocket says so, and without these credentials.
          if (socketLink.generation !== startedIn) return;

          // Compare-and-swap, as the session guard does for the AuthStore's
          // refresh (stores/sessionGuard.ts): if another tab signed out or in
          // while this refresh was in flight, the shared refresh token is no
          // longer the one it spent, and its access token is not written over
          // whoever's session storage now holds.
          const superseded =
            window.localStorage.getItem(resolvedRefreshStorageKey) !==
            firstLoadAuth.refreshToken;
          if (superseded) {
            // Leave storage, and this tab's AuthStore, to the session that
            // replaced it (the AuthStore hears the other tab's storage events).
            // The handshake below must not present what this refresh was
            // spending, though: it is taken from the storage that replaced it
            // (or dropped, if that session signed out).
          } else if (newAccessToken) {
            // Store the new token in localStorage
            window.localStorage.setItem(resolvedAuthStorageKey, newAccessToken);
            if (debugMode) {
              console.warn(
                "apiClient debugMode: connecting socket with refreshed token",
              );
            }
          } else {
            // The pre-connect refresh failed and we are about to open the socket
            // with a refresh token alone. This is the exact shape of the
            // reconnect-auth hang that reached a human as "the screen is frozen"
            // - with no sink installed it was a console line nobody saw.
            emitAppDiagnostic(
              "warn",
              "apiClient",
              "auth.refresh_failed",
              "Pre-connect token refresh failed; opening the socket with the refresh token only",
              { data: { mode } },
            );
            console.warn(
              "Token refresh failed, connecting with refresh token only",
            );
            // Keep the refresh token in firstLoadAuth so backend can use it
          }

          // The handshake is whatever storage says now: the refreshed token,
          // the session that replaced this one, or nothing.
          installHandshake();
          // What those calls queued meanwhile (socket.io parks an emit on a
          // socket that is not connected) is settled for the handshake just
          // installed, before the connect flushes it: a session that was
          // replaced or ended during the refresh must not have the previous
          // person's calls or re-authentication delivered on its connection.
          settleBuffer({ subjectBefore: signedInSubject ?? loadedSubject });
        } catch (error) {
          // Storage unreadable or the like: connect, but never under credentials
          // that may no longer be this tab's.
          emitAppDiagnostic(
            "warn",
            "apiClient",
            "auth.refresh_failed",
            "Pre-connect token refresh threw; opening the socket without credentials",
            { error },
          );
          clearHandshake();
          settleBuffer({ anonymous: true, subjectBefore: signedInSubject ?? loadedSubject });
        }
        // The one connect of the first load, on every branch above.
        if (socketLink.generation !== startedIn) return;
        socket.connect();
      })();
    }

    // Reconnection attempts this client cancelled to refresh the token, since
    // the last connection. socket.io resets its own backoff every time one is
    // cancelled (the close of the attempt resets it), so without this count an
    // outage with an expired token made a refresh request and a connection
    // attempt about once a second for as long as it lasted.
    let cancelledAttempts = 0;
    socket.on("connect", () => {
      cancelledAttempts = 0;
    });

    // A HANDSHAKE THE SERVER REFUSED IS FINAL FOR socket.io-client. When a
    // server middleware answers next(err) — the API's socket security does,
    // past its per-address cap or its connect-rate window
    // (packages/api/src/channelSystem/security/socketSecurity.ts), which an
    // office behind one address can reach when every tab reconnects after a
    // deploy — the client gets `connect_error` and DESTROYS the socket
    // (`active` false). Neither socket.io nor the library's ConnectionStore
    // (it retries `io server disconnect` only) tries again, so the tab held no
    // connection at all — no live updates, every call queued — until it was
    // reloaded (#3820; the libraries' realtime audit, host step 4). A refused
    // handshake is retried here, on socket.io's base delay doubled per refusal
    // up to a minute, until one is accepted. Never while suspended: a sign-out
    // or a foreign credential tore the socket down, and resumeSocket owns the
    // connect that brings it back.
    let refusedAttempts = 0;
    let refusedTimer: ReturnType<typeof setTimeout> | null = null;
    /** The link generation the pending retry was scheduled in. */
    let refusedGeneration = -1;
    const refusedRetryDelay = (attempt: number): number => {
      let base = 1000;
      try {
        const value = (socket.io as unknown as { reconnectionDelay?: () => unknown }).reconnectionDelay?.();
        if (typeof value === "number" && Number.isFinite(value) && value > 0) base = value;
      } catch {
        // keep the default
      }
      return Math.min(base * 2 ** attempt, REFUSED_HANDSHAKE_RETRY_MAX_MS);
    };
    const readStorage = (key: string): string | null => {
      try {
        return window.localStorage.getItem(key);
      } catch {
        return null;
      }
    };
    socket.on("connect", () => {
      refusedAttempts = 0;
      if (refusedTimer) {
        clearTimeout(refusedTimer);
        refusedTimer = null;
      }
    });
    socket.on("connect_error", () => {
      // `active`: socket.io is still retrying this one itself (a transport error).
      if (socket.active || socketLink.suspended) return;
      if (refusedTimer) {
        // Pending for this connection: it will retry.
        if (refusedGeneration === socketLink.generation) return;
        // Scheduled before a suspension (and the resume since): it stands down
        // when it fires, so it must not stand in for this refusal.
        clearTimeout(refusedTimer);
        refusedTimer = null;
        refusedAttempts = 0;
      }
      const wait = refusedRetryDelay(refusedAttempts);
      refusedAttempts += 1;
      // A suspension (and any resume after it) bumps the generation: the
      // connection is then resumeSocket's, as it is for the refresh below.
      refusedGeneration = socketLink.generation;
      const startedIn = refusedGeneration;
      refusedTimer = setTimeout(() => {
        refusedTimer = null;
        if (socketLink.generation !== startedIn || socketLink.suspended) return;
        // Somebody else is already connecting it: socket.io, or the reconnect
        // refresh below (which connects when it settles).
        if (socket.connected || socket.active || isRefreshing) return;
        // An expired stored token is refreshed first, exactly as a reconnect
        // attempt does it: no connection opens under a token a refresh could
        // replace (a refusal can outlast the token: a full per-address cap).
        const token = readStorage(resolvedAuthStorageKey);
        const refreshToken = readStorage(resolvedRefreshStorageKey);
        const userId = readStorage(userIdStorageKey);
        const deviceId = readStorage("deviceId");
        if (token && isTokenExpired(token) && refreshToken && userId && deviceId) {
          void refreshThenConnect({ refreshToken, userId, deviceId }, () => 0);
          return;
        }
        // What every other connect this client makes does first: present what
        // the shared storage says NOW (the person may have refreshed, or signed
        // in elsewhere, since this socket was refused), with the send buffer
        // settled for that handshake before the connect flushes it.
        if (apiOptions.enableSocketAuth) {
          installHandshake();
          settleBuffer();
        }
        socket.connect();
      }, wait);
    });

    /**
     * What socket.io's own backoff would wait before attempt `attempt`:
     * min(base * 2^attempt, max), with the manager's jitter, read from the
     * manager so a tuned client keeps its tuning.
     */
    const reconnectDelay = (attempt: number): number => {
      const manager = socket.io as unknown as Record<string, ((value?: number) => unknown) | undefined>;
      const read = (name: string, fallback: number): number => {
        try {
          const value = manager[name]?.call(manager);
          return typeof value === "number" && Number.isFinite(value) ? value : fallback;
        } catch {
          return fallback;
        }
      };
      const base = read("reconnectionDelay", 1000);
      const max = read("reconnectionDelayMax", 5000);
      const jitter = read("randomizationFactor", 0.5);
      const wait = Math.min(base * 2 ** attempt, max);
      return Math.max(0, Math.min(max, Math.round(wait + (Math.random() * 2 - 1) * jitter * wait)));
    };

    // Handle reconnection. socket.io does not wait for this listener: it
    // announces the attempt and opens the transport in the same tick, with
    // whatever `socket.io.opts.query` and `socket.auth` hold at that moment.
    // So the part that decides what the attempt presents is SYNCHRONOUS, and
    // the refresh (which cannot be) cancels the attempt and reconnects by hand.
    let isRefreshing = false;
    /**
     * Refresh the stored access token, then connect with the handshake storage
     * holds once the refresh has settled. THE one place a connection waits on
     * a refresh: a reconnect attempt that found an expired token (below) and
     * the retry of a refused handshake (above) both come through here, so
     * neither opens a connection under a token a refresh could replace.
     * `waitBeforeConnect` is the back-off the caller owes before the connect.
     * A no-op while a refresh is already in flight: that one connects.
     */
    const refreshThenConnect = (
      credentials: { refreshToken: string; userId: string; deviceId: string },
      waitBeforeConnect: () => number,
    ): Promise<void> | undefined => {
      if (isRefreshing) return undefined;
      isRefreshing = true;
      const startedIn = socketLink.generation;
      return (async () => {
        try {
          const newToken = await refreshAccessToken(
            uri,
            credentials.refreshToken,
            credentials.userId,
            credentials.deviceId,
          );
          // Suspended (or suspended and resumed) meanwhile: resumeSocket owns
          // the connection, and it connects without these credentials.
          if (socketLink.generation !== startedIn) return;

          if (!newToken) {
            emitAppDiagnostic(
              "warn",
              "apiClient",
              "auth.refresh_failed",
              "Reconnect token refresh failed; reconnecting with the expired token",
              { data: { mode } },
            );
          }
          // Compare-and-swap (see the first-load refresh above): another tab
          // that signed out or in meanwhile owns storage now.
          const superseded = readStorage(resolvedRefreshStorageKey) !== credentials.refreshToken;
          if (!superseded && newToken) {
            window.localStorage.setItem(resolvedAuthStorageKey, newToken);
            if (debugMode) {
              console.log("Reconnecting with refreshed token");
            }
          }
          // Back off before the connection, as socket.io would have between
          // attempts had this one not been cancelled (see cancelledAttempts).
          // The handshake is decided AFTER the wait, from what storage holds
          // then.
          const wait = waitBeforeConnect();
          if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait));
          if (socketLink.generation !== startedIn) return;

          // The handshake is the session in storage now: the refreshed token,
          // the one that replaced this one, or nothing - never the credentials
          // this refresh spent. (Where the client presents any at all, or
          // there is a refreshed token to present.)
          if (apiOptions.enableSocketAuth || (!superseded && newToken)) {
            installHandshake();
            settleBuffer();
          }
        } catch (error) {
          emitAppDiagnostic(
            "warn",
            "apiClient",
            "auth.refresh_failed",
            "Reconnect token refresh threw; reconnecting without credentials",
            { error },
          );
          clearHandshake();
          settleBuffer({ anonymous: true });
        } finally {
          isRefreshing = false;
        }
        // The one connection of this attempt, on every branch above.
        if (socketLink.generation !== startedIn) return;
        socket.connect();
      })();
    };
    socket.io.on("reconnect_attempt", () => {
      // Torn down by suspendSocket: nothing reconnects under the old auth.
      if (socketLink.suspended) return;

      // 1. Present what the shared storage says NOW. Not the page-load
      // handshake: the person may have signed out here, or another tab may
      // have signed in as someone else, since it was built. (Only where the
      // client presents credentials at all.)
      if (apiOptions.enableSocketAuth) installHandshake();

      const currentToken = readStorage(resolvedAuthStorageKey);
      const currentRefreshToken = readStorage(resolvedRefreshStorageKey);
      const currentUserId = readStorage(userIdStorageKey);
      const currentDeviceId = readStorage("deviceId");
      const needsRefresh =
        !!currentToken &&
        isTokenExpired(currentToken) &&
        !!currentRefreshToken &&
        !!currentUserId &&
        !!currentDeviceId;
      // The stored access token is good: this attempt goes out with what step
      // 1 installed - and with the buffer settled for it, since the calls
      // waiting in it were made under whoever the last connection was.
      if (!needsRefresh && !isRefreshing) {
        if (apiOptions.enableSocketAuth) settleBuffer();
        return;
      }

      // 2. The stored access token is expired: this attempt must not go out
      // with it (or with anything the refresh may be about to replace).
      // Cancel it. disconnect() ends the pending attempt - socket.io checks
      // `skipReconnect` right after announcing it - and a later connect()
      // starts a fresh one with the handshake installed by the refresh.
      socket.disconnect();
      if (isRefreshing) return; // the refresh in flight connects when it settles

      return refreshThenConnect(
        { refreshToken: currentRefreshToken!, userId: currentUserId!, deviceId: currentDeviceId! },
        () => {
          const wait = reconnectDelay(cancelledAttempts);
          cancelledAttempts += 1;
          return wait;
        },
      );
    });

    // Add connection event handlers for better debugging
    if (debugMode) {
      socket.on("connect", () => {
        console.warn("apiClient debugMode: Socket connected");
      });

      socket.on("disconnect", (reason) => {
        console.warn("apiClient debugMode: Socket disconnected:", reason);
      });

      socket.on("connect_error", (error) => {
        console.warn("apiClient debugMode: Socket connection error:", error);
      });
    }

    if (debugMode) {
      console.warn("apiClient debugMode: socket", socket);
    }

    const socketConnection = socketio(socket, {
      timeout: 100000,
    });

    // connectionInstances[mode] = feathers()
    //   .configure(
    //     socketio(socket, {
    //       timeout: 100000,
    //     })
    //   )
    //   .configure(authentication(authOptions));

    const newClient = createClient(socketConnection, authOptions);

    // Only log in debug mode
    if (debugMode) {
      console.warn("apiClient debugMode: newClient", newClient);
    }
    connectionInstances[mode] = newClient;

    // Who the connection is signed in as, as the client itself says it (see
    // signedInSubject): the access token's subject, else the user's id.
    newClient.on("login", (result: { accessToken?: unknown; user?: { id?: unknown } | null } | undefined) => {
      const fromUser = result?.user?.id;
      signedInSubject =
        tokenSubject(result?.accessToken) ?? (fromUser == null ? undefined : String(fromUser));
    });
    newClient.on("logout", () => {
      signedInSubject = undefined;
    });
    // The authentication client re-authenticates when the socket drops and does
    // not wait for the answer. If the reconnect fails that call because it
    // belonged to a session that ended (the buffer was settled), nobody is
    // left to hear it: say so quietly rather than as an unhandled rejection.
    // The failure is only marked handled, never rethrown from a derived
    // promise (which nobody handles, and which raised a global unhandled
    // rejection for every other failure: a 401, a 429, a timeout). Whoever
    // awaits the promise still sees its own rejection. Once per promise.
    const quieted = new WeakSet<object>();
    socket.on("disconnect", () => {
      const pending = newClient.get("authentication") as Promise<unknown> | undefined;
      if (pending && typeof pending.catch === "function" && !quieted.has(pending)) {
        quieted.add(pending);
        pending.catch(() => undefined);
      }
    });

    // if (enableFindOne) {
    //   connectionInstances[mode].mixins.push(function (service: any) {
    //     service.findOne = function (params: any) {
    //       params = params || {};
    //       params.query = params.query || {};
    //       params.query.$limit = 1;
    //       return service.find(params).then(function (result: any) {
    //         const data = result.data || result;
    //         return Array.isArray(data) ? data[0] : data;
    //       });
    //     };
    //   });
    // }

    if (debugMode) {
      console.warn(
        "apiClient debugMode: connectionInstances[mode]",
        mode,
        connectionInstances[mode],
      );
    }
    if (
      socketAuthentication &&
      connectionInstances[mode] &&
      "set" in connectionInstances[mode]
    ) {
      connectionInstances[mode].set("socketAuthPending", true);
      if (debugMode) {
        console.warn(
          'apiClient debugMode: connectionInstances[mode].set("socketAuthPending", true);',
        );
      }
    }
    return connectionInstances[mode];
  }
};

/**
 * export default apiClient
 * @param {String} mode Server Mode: dev, development, stage, beta, prod, production
 * @param {Object} apiOptions
 *  Default:
 *  {
 *    debugMode: false,
 *    authStorageKey: 'feathers-jwt-dev',
 *    refreshStorageKey: 'refresh-token-dev',
 *    enableSocketAuth: true,
 *    restClient: false,
 *    enableFindOne: false
 *  }
 * @returns Api Client
 */
export default apiClient;
